import { Children, ReactNode, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { useDescriptionVariant } from "../description/use-description-variant";
import { StaticDescriptionContent, TooltipDescription } from "../description";
import { cn } from "../../utils/class-names";

interface KeyValueListProps {
  className?: string;
  divider?: boolean;
  children?: ReactNode;
}

export const KeyValueList = ({
  className,
  children,
  divider = false,
}: KeyValueListProps): JSX.Element => {
  const array = Children.toArray(children);

  return (
    <dl className={twMerge("flex flex-col gap-5", className)}>
      {array.map((child, i) => (
        <>
          {divider && i > 0 && <div className="h-px bg-shale-700 lg:hidden" />}

          {child}
        </>
      ))}
    </dl>
  );
};

KeyValueList.Pair = Pair;
KeyValueList.Key = Key;
KeyValueList.Value = Value;

interface KeyValuePairProps {
  className?: string;
  description?: ReactNode;
  trailingContent?: ReactNode;
  trailingContentContainerClassName?: string;
  forceStaticDescription?: boolean;
  children?: ReactNode;
}

function Pair({
  className,
  children,
  description,
  trailingContent,
  trailingContentContainerClassName,
  forceStaticDescription = false,
}: KeyValuePairProps): JSX.Element {
  const array = Children.toArray(children);

  useEffect(() => {
    if (array.length >= 3) {
      console.warn("Do not pass more than 2 child elements to KeyValuePair");
    }
  }, [array.length]);

  const [firstChild, secondChild] = array;

  const descriptionVariant = useDescriptionVariant();
  const showTooltipDescription =
    description && descriptionVariant === "tooltip" && !forceStaticDescription;
  const showStaticDescription =
    description && (descriptionVariant === "static" || forceStaticDescription);

  return (
    <div className="flex flex-col gap-5">
      <div
        className={cn(
          "flex flex-col gap-1.5",
          trailingContentContainerClassName
        )}
      >
        <div
          className={twMerge(
            "flex gap-2.5 items-baseline justify-between",
            className
          )}
        >
          <div className="flex gap-1.5 items-center">
            <span>{firstChild}</span>

            {showTooltipDescription && (
              <TooltipDescription>{description}</TooltipDescription>
            )}
          </div>

          {secondChild}
        </div>

        <Slot>{trailingContent}</Slot>
      </div>

      {showStaticDescription && (
        <StaticDescriptionContent>{description}</StaticDescriptionContent>
      )}
    </div>
  );
}

interface KeyProps {
  className?: string;
  dataCy?: string;
  children?: ReactNode;
}

function Key({ className, dataCy, children }: KeyProps): JSX.Element {
  return (
    <dt data-cy={dataCy} className={twMerge("text-shale-200", className)}>
      {children}
    </dt>
  );
}

interface ValueProps {
  className?: string;
  dataCy?: string;
  children?: ReactNode;
}

function Value({ className, children, dataCy }: ValueProps): JSX.Element {
  return (
    <dd
      data-cy={dataCy}
      className={twMerge(
        "text-white font-mono font-medium flex flex-col items-end",
        className
      )}
    >
      {children}
    </dd>
  );
}

interface SlotProps {
  className?: string;
  children?: ReactNode;
}

function Slot({ className, children }: SlotProps): JSX.Element | null {
  if (!children) return null;

  return <div className={twMerge("empty:hidden", className)}>{children}</div>;
}
